import type { FC } from 'react';
import React from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';
import { css, styled } from '@compiled/react';

import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { durations } from '@atlaskit/motion';
import { layers } from '@atlaskit/theme/constants';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { useModernizeBanner } from '@confluence/modernize-confluence-ipm-modal/entry-points/ModernizeBanner.tsx';
import { useOnboardingTakeoverType } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/useOnboardingTakeoverType.ts';
import { useWebPanelLocation } from '@confluence/web-panel-location/entry-points/useWebPanelLocation.ts';
import {
	FEED_EXPERIENCE,
	HOME_OVERVIEW_RECENT_EXPERIENCE,
	HOME_OVERVIEW_EXPERIENCE,
	HOME_RIGHT_PANEL_APPS_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { collectAll } from '@confluence/experience-tracker/entry-points/ExperienceEventCollector.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { WebPanel } from '@confluence/web-panel-location/entry-points/WebPanel.ts';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart.ts';
import { HOME_OVERVIEW_FEED_PAGE_SEGMENT } from '@confluence/feed/entry-points/perf.config.ts';
import { Feed } from '@confluence/feed/entry-points/Feed.ts';
import type { CustomPanel } from '@confluence/feed/entry-points/Feed.ts';
import { DASHBOARD_SECONDARY } from '@confluence/web-item-location/entry-points/webFragmentLocations.ts';
import { useTemplatesCollectionEligible } from '@confluence/onboarding-helpers/entry-points/hooks/useTemplatesCollectionEligible.ts';
import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';
import { PostOfficeHomeBanner } from '@confluence/experiment-post-office-home-banner/entry-points/PostOfficeHomeBanner.ts';
import { usePostOfficeHomeBannerEligible } from '@confluence/experiment-post-office-home-banner/entry-points/usePostOfficeHomeBannerExperiment.ts';
import { LazyAutomationExecutionLimit } from '@confluence/automation/entry-points/execution-limit.ts';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { useRovoAwarenessBannerDisplay } from '@confluence/rovo-awareness/entry-points/useRovoAwarenessBannerDisplay.ts';
import { expValEquals } from '@confluence/feature-experiments';

import {
	HOME_OVERVIEW_RECENT_PAGE_SEGMENT,
	HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT,
} from '../../../perf.config.ts';
import { RightPanel } from '../../RightPanel/RightPanel.tsx';
import { useRightPanelVisible } from '../../RightPanel/useRightPanelVisible.ts';
import { HomeWelcomeMessage } from '../../RightPanel/WelcomeMessage/LazyWelcomeMessage.ts';
import { Calendars } from '../../RightPanel/Calendars.tsx';
import {
	LazyFeedbackIcon as FeedbackIcon,
	LazyCalendarIcon as CalendarIcon,
} from '../images/lazyImages.tsx';

import { HomeBannerContainer } from './HomeBannerContainer.tsx';
import { RecentList } from './RecentList.tsx';
import { useIsEligibleForHideRecents } from './useIsEligibleForHideRecents.tsx';

const i18n = defineMessages({
	homeTitle: {
		id: 'home.title',
		defaultMessage: 'Home',
		description: 'Title for the home page',
	},
	forYouTitle: {
		id: 'home.for-you.title',
		defaultMessage: 'For you',
		description: 'Title for the home page which is called "For you" in nav 4',
	},
	recentWork: {
		id: 'home.views.overview.recent-work',
		defaultMessage: 'Pick up where you left off',
		description: 'Header for a list of users recently visited pages',
	},
	calendarTitle: {
		id: 'home.feed.calendar.title',
		defaultMessage: 'Calendars',
		description: "Title for home's calendar tab",
	},
	announcementsTitle: {
		id: 'home.feed.announcements.title',
		defaultMessage: 'Announcements',
		description: "Title for home's annuncements tab",
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const OverviewContainer2 = styled.div<{
	isOnboardingExperienceEligible: boolean;
	isPostOfficeHomeBannerEligible: boolean;
	isModernizeBannerEligible: boolean;
	isRovoAwarenessBannerEligible: boolean;
	shouldShowRightPanel?: boolean;
}>({
	width: '100%',
	display: 'grid',
	gridColumnGap: token('space.400'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridTemplateRows: ({ isPostOfficeHomeBannerEligible }) =>
		isPostOfficeHomeBannerEligible ? 'auto auto auto 1fr' : 'auto auto 1fr',
	/* If --homeIsSingleColumn == 0 then include a 300px sidebar */
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	gridTemplateColumns: ({ shouldShowRightPanel }) =>
		shouldShowRightPanel
			? `calc((100% - calc((1 - var(--homeIsSingleColumn)) * ${token('space.400')}) - (1 - var(--homeIsSingleColumn)) * 300px)) calc((1 - var(--homeIsSingleColumn)) * 300px)`
			: '100% 0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	transition: ({ shouldShowRightPanel }) =>
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
		shouldShowRightPanel ? `${durations.medium}ms` : 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	'--home-has-onboarding-experience': ({ isOnboardingExperienceEligible }) =>
		isOnboardingExperienceEligible ? '1' : '0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	'--home-show-post-office-banner': ({ isPostOfficeHomeBannerEligible }) =>
		isPostOfficeHomeBannerEligible ? '1' : '0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	'--home-show-modernize-banner': ({ isModernizeBannerEligible }) =>
		isModernizeBannerEligible ? '1' : '0',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	'--home-show-rovo-awareness-banner': ({ isRovoAwarenessBannerEligible }) =>
		isRovoAwarenessBannerEligible ? '1' : '0',
});

const recentContainerStyles = css({
	position: 'relative',
	gridColumn: '1 / span 1',
	// Above Condition and If --home-show-template-collections == 1 then row = 4 else row = 3

	gridRow:
		'(1 + var(--home-has-onboarding-experience) + var(--home-show-template-collections) + var(--home-show-modernize-banner) + var(--home-show-rovo-awareness-banner)) / span 1',
	marginTop: '0px',

	marginBottom: token('space.300'),
});

const bannerContainerStyles = css({
	gridColumn: '1 / span 1',
	// If --homeIsSingleColumn == 1 then row = 3 else row = 2
	// Above Condition and If --home-has-onboarding-experience == 1 then row = 4 else row = 3

	gridRow: `calc(
			2 + var(--homeIsSingleColumn) + var(--home-has-onboarding-experience) +
				var(--home-show-template-collections) + var(--home-show-modernize-banner) +
				var(--home-show-rovo-awareness-banner)
		) / span 1`,
});

const feedContainerStyles = css({
	gridColumn: '1 / span 1',
	// If --homeIsSingleColumn == 1 then row = 3 else row = 2
	// Above Condition and If --home-has-onboarding-experience == 1 then row = 4 else row = 3
	// Above Condition and If --home-show-post-office-banner == 1 then row = 5 else row = 4

	gridRow: `calc(
			2 + var(--homeIsSingleColumn) + var(--home-has-onboarding-experience) +
				var(--home-show-template-collections) + var(--home-show-post-office-banner)
				+ var(--home-show-modernize-banner) + var(--home-show-rovo-awareness-banner)
		) / span 1`,
});

const rightPanelContainerStyles = css({
	marginTop:
		'calc(34px * (1 - var(--homeIsSingleColumn)) * (1 - var(--home-has-onboarding-experience)))',
	transition: 'margin-top 0.5s',
	// If --homeIsSingleColumn == 1 then col = 1 else col = 2

	gridColumn: `calc(2 - var(--homeIsSingleColumn)) / span 1`,
	// If --homeIsSingleColumn == 1 then row = 2 / span 1 else row = 1 / span 2

	gridRow: `calc(
			1 + (1 + var(--home-has-onboarding-experience) + var(--home-show-template-collections)) *
				var(--homeIsSingleColumn)
		) / span calc(3 - 2 * var(--homeIsSingleColumn))`,
	minWidth: '300px',
});

const recentSpotlightContainerStyles = css({
	position: 'absolute',
	width: '100%',
	height: '100%',
	left: token('space.negative.050'),
	padding: `${token('space.050')} ${token('space.050')} ${token('space.025')}`,
	pointerEvents: 'none',
});

const recentHeaderStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	marginBottom: token('space.200'),
});

const limitContainerStyles = css({
	position: 'fixed',
	left: token('space.1000'),
	zIndex: layers.modal() + 50,
	bottom: token('space.1000'),
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: token('radius.small'),
});

const collectOverview = (events, experience) => {
	const collection = [
		FEED_EXPERIENCE,
		HOME_OVERVIEW_RECENT_EXPERIENCE,
		HOME_RIGHT_PANEL_APPS_EXPERIENCE,
	];
	collectAll(collection)(events, experience);
};

const RecentHeader = () => (
	<>
		<SpotlightTarget name="home-recent">
			<div css={recentSpotlightContainerStyles} />
		</SpotlightTarget>
		<div css={recentHeaderStyles}>
			<Heading size="xsmall" as="h2">
				<FormattedMessage {...i18n.recentWork} />
			</Heading>
		</div>
	</>
);

//previously called Overview or homeOverview
export const ForYou: FC = () => {
	const intl = useIntl();
	const isNav4Enabled = useIsNav4Enabled();

	const { onboardingTakeoverType, loading: isOnboardingTakeoverTypeLoading } =
		useOnboardingTakeoverType();
	const { isTemplatesCollectionEligible } = useTemplatesCollectionEligible();
	const { isEligible: isPostOfficeHomeBannerEligible } = usePostOfficeHomeBannerEligible();
	const { shouldShowRightPanel } = useRightPanelVisible();
	const { isVisible: isModernizeBannerEligible, loading: isModernizeBannerLoading } =
		useModernizeBanner();
	const shouldShowRovoBanner = useRovoAwarenessBannerDisplay({
		isModernizeBannerEligible,
		onboardingTakeoverType,
		skip: isModernizeBannerLoading || isOnboardingTakeoverTypeLoading,
	});

	const isEligibleForHideRecents = useIsEligibleForHideRecents();

	const {
		panels,
		loading: appsLoading,
		error: appsError,
	} = useWebPanelLocation({
		//no way to conditionally load this? Because hooks can't be conditional
		location: DASHBOARD_SECONDARY,
		fetchPolicy: 'cache-first',
	});

	const feedCustomWebSections =
		(panels &&
			panels
				.sort(({ weight: i }, { weight: j }) => (i > j ? 1 : -1))
				.map(({ html, completeKey, label }) =>
					completeKey.includes('team-calendars')
						? {
								title: intl.formatMessage(i18n.calendarTitle),
								key: completeKey,
								icon: CalendarIcon,
								content: <Calendars completeKey={completeKey} key={completeKey} noCollapse />,
							}
						: {
								title: label,
								icon: undefined,
								key: completeKey,
								content: <WebPanel html={html} />,
							},
				)) ||
		[];

	const feedCustomPanels: CustomPanel[] = [
		{
			title: intl.formatMessage(i18n.announcementsTitle),
			key: 'confluence.home.announcements',
			// @ts-ignore TODO: fix the CustomPanel type to accept LoadableType icons
			icon: FeedbackIcon,
			content: <HomeWelcomeMessage noCollapse />, //also has onLoadProp for experience tracking?
		},
		// @ts-ignore TODO: fix the CustomPanel type to accept LoadableType icons
		...feedCustomWebSections,
	];

	return (
		<>
			<OverviewContainer2
				isOnboardingExperienceEligible={Boolean(
					onboardingTakeoverType || isTemplatesCollectionEligible, // Only one of the two can show up at once, so we only need one styling variable
				)}
				isPostOfficeHomeBannerEligible={isPostOfficeHomeBannerEligible}
				shouldShowRightPanel={shouldShowRightPanel}
				isModernizeBannerEligible={isModernizeBannerEligible}
				isRovoAwarenessBannerEligible={shouldShowRovoBanner}
				data-testid="home_overview"
			>
				<DocumentContentTitle
					titlePrefix={
						isNav4Enabled
							? intl.formatMessage(i18n.forYouTitle)
							: intl.formatMessage(i18n.homeTitle)
					}
					isHomePage
				/>
				<ExperienceStart name={HOME_OVERVIEW_EXPERIENCE} collect={collectOverview} />
				<ExperienceStart name={HOME_OVERVIEW_RECENT_EXPERIENCE} timeout={ExperienceTimeout.HOME} />
				<UFOSegment name="banners">
					<HomeBannerContainer
						onboardingTakeoverType={onboardingTakeoverType}
						isTemplatesCollectionEligible={isTemplatesCollectionEligible}
						isModernizeBannerEligible={isModernizeBannerEligible}
						shouldShowRovoBanner={shouldShowRovoBanner}
					/>
					{!(
						isEligibleForHideRecents &&
						expValEquals('cc-hide-pickup-where-you-left-off-exp', 'cohort', 'experiment')
					) && (
						<div css={recentContainerStyles}>
							<PageSegmentLoadStart metric={HOME_OVERVIEW_RECENT_PAGE_SEGMENT} />
							<>
								<RecentHeader />
								<RecentList />
							</>
						</div>
					)}
					{isPostOfficeHomeBannerEligible && (
						<div css={bannerContainerStyles}>
							<PostOfficeHomeBanner />
						</div>
					)}
				</UFOSegment>
				<UFOSegment name="feed-section">
					<div css={feedContainerStyles}>
						<PageSegmentLoadStart metric={HOME_OVERVIEW_FEED_PAGE_SEGMENT} />
						<ExperienceStart name={FEED_EXPERIENCE} />
						<ExperienceStart name={HOME_RIGHT_PANEL_APPS_EXPERIENCE} />
						<Feed renderPopularFeed customPanels={feedCustomPanels} />
						{!appsLoading && !appsError ? (
							<ExperienceSuccess name={HOME_RIGHT_PANEL_APPS_EXPERIENCE} />
						) : null}
					</div>
				</UFOSegment>
				{shouldShowRightPanel && (
					<div css={rightPanelContainerStyles}>
						<PageSegmentLoadStart metric={HOME_OVERVIEW_RIGHT_PANEL_PAGE_SEGMENT} />
						<RightPanel widthMode="fullwidth" />
					</div>
				)}
			</OverviewContainer2>
			<UFOSegment name="automation-limit">
				<div css={limitContainerStyles}>
					<LazyAutomationExecutionLimit />
				</div>
			</UFOSegment>
		</>
	);
};
